import {
  Community,
  CommunityFeed as CommunityFeedType,
  OrgBootstrap,
} from "@dreambigger/shared/src/types";
import {
  communityApi,
  orgApi,
} from "@dreambigger/shared/src/api/weserve/api-helper";
import { GetStaticProps } from "next";
import { useTryAsync } from "no-try";
import { CommunitySlugParams } from "../../utils/static-paths-helper";
import BoardWrapper from "../boards/board-wrapper";
import { is404 } from "@dreambigger/shared/src/utils";

/**
 * Pre-render the page with the following data from the Weserve API.
 *  - Community: Response from GET /communities (General Info to use throughout the page, i.e Community Meta)
 *  - CommunityFeed: Response from GET /communities/:slug/feed (Community Cards on page)
 *  - OrgBootstrap: Response from GET /organizations/:id/bootstrap (Footer and Header)
 */

export type CommunityProps = {
  community: Community;
  communityFeed: CommunityFeedType;
  orgBootstrap: OrgBootstrap;
};

export const DEFAULT_STATIC_REVALIDATION_SECONDS = 120;

const CommunityFeed = (props: CommunityProps) => {
  return (
    <BoardWrapper
      {...props}
      initialData={[props.communityFeed]}
      community={props.community}
      orgBootstrap={props.orgBootstrap}
      boardType="feed"
    />
  );
};

// Get static props data for the main community feed page.
export const getCommunityFeedStaticProps: GetStaticProps<
  CommunityProps,
  CommunitySlugParams
> = async (context) => {
  const { communitySlug } = context.params!;
  // Get the community. If it doesn't exist return a 404 page.
  const [error, communityResponse] = await useTryAsync(() =>
    communityApi.getBySlug(communitySlug)
  );

  // handle 404s during ISR gracefully
  if (error && is404(error)) {
    console.error("Unable to find community:", error);
    return {
      notFound: true,
    };
  }

  // throw error to ensure statically generated page is not replaced from CDN during ISR
  if (error) {
    throw error;
  }
  // Add a null check before accessing communityResponse.data
  if (!communityResponse || !communityResponse.data) {
    throw new Error("Community response is null or undefined");
  }

  const community = communityResponse.data;

  // The amount in seconds we want to rehydrate static pages with new content.
  const regenerationTime =
    community.meta?.options?.staticGeneration?.revalidate ||
    DEFAULT_STATIC_REVALIDATION_SECONDS;

  // Get the organization's bootstrap data.
  const orgBootstrapResponse = await orgApi.getOrgBootstrap(
    community.organization.id
  );
  const orgBootstrap = orgBootstrapResponse.data;

  // Get the community's feed.
  const feedResponse = await communityApi.getFeed(communitySlug);
  const communityFeed = feedResponse.data;

  /**
   * The data we want to pre-render for each community.
   * Next.js will attempt to regenerate the page at most every "n" seconds
   * based on the value passed to revalidate.
   */
  return {
    props: {
      community,
      communityFeed,
      orgBootstrap,
    },
    revalidate: regenerationTime,
  };
};

export default CommunityFeed;
